// src/features/bootstrap/StepForms.tsx
// One form per wizard step (mode, identity, vault, admin, products, security).
// Each form saves its step (Save*), can validate without saving (ValidateStep),
// and — for vault/security — can test connectivity before saving.
// See docs/ux/bootstrap-ia.md section 1.

import { useState } from 'react';
import { useI18n, useBootstrap } from '@react/hooks';
import { Button, Input, Textarea, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ValidateSlug } from '@core/api/endpoints/bootstrap/validate_slug';
import { DeploymentModeValues, DataResidencyRegionValues, AutoUnsealProviderValues, AuthMethodTypeValues, JwtSigningAlgorithmValues } from '@core/types/bootstrap';
import type {
  PlatformModeConfig, DeploymentMode,
  PlatformIdentityConfig, DataResidencyRegion,
  VaultInitConfig, AutoUnsealProvider, AutoUnsealConfig,
  AdminAccountConfig,
  ProductSelectionConfig,
  SecurityConfig, AuthMethodType, JwtSigningAlgorithm, LdapConfig, OidcConfig,
} from '@core/types/bootstrap';
import { enumSuffix } from './logic';

function ValidationErrors({ errors }: { errors: string[] }) {
  if (errors.length === 0) return null;
  return (
    <ul className="list-disc pl-5 text-sm text-destructive space-y-0.5">
      {errors.map((e) => <li key={e}>{e}</li>)}
    </ul>
  );
}

function StepFooter({ isSaving, isValidating, onValidate, savedLabel }: {
  isSaving: boolean; isValidating: boolean; onValidate: () => void; savedLabel: string;
}) {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2">
      <Button type="submit" size="sm" disabled={isSaving}>{isSaving ? t('common.saving') : savedLabel}</Button>
      <Button type="button" variant="secondary" size="sm" onClick={onValidate} disabled={isValidating}>
        {isValidating ? t('common.loading') : t('bootstrap.step.validate')}
      </Button>
    </div>
  );
}

// --- Step 1: Mode ---

export function ModeStepForm({ initial, onSaved }: { initial?: PlatformModeConfig; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [mode, setMode] = useState<DeploymentMode>(initial?.mode ?? 'DEPLOYMENT_MODE_CLOUD');
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await store.SavePlatformMode({ mode: { mode } });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const r = await store.ValidateStep('mode', { step_id: 'mode', mode: { mode } });
      setErrors(r.errors ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <Select
        label={t('bootstrap.step.mode.field')}
        value={mode}
        onChange={(e) => setMode(e.target.value as DeploymentMode)}
        options={DeploymentModeValues.filter((v) => v !== 'DEPLOYMENT_MODE_UNSPECIFIED').map((v) => ({
          value: v, label: t(`bootstrap.step.mode.option.${enumSuffix(v, 'DEPLOYMENT_MODE_')}`),
        }))}
      />
      <ValidationErrors errors={errors} />
      <StepFooter isSaving={isSaving} isValidating={isValidating} onValidate={handleValidate} savedLabel={t('common.save')} />
    </form>
  );
}

// --- Step 2: Identity ---

export function IdentityStepForm({ initial, onSaved }: { initial?: PlatformIdentityConfig; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [domain, setDomain] = useState(initial?.domain ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [dataResidency, setDataResidency] = useState<DataResidencyRegion>(initial?.data_residency ?? 'DATA_RESIDENCY_REGION_GLOBAL');
  const [defaultLocale, setDefaultLocale] = useState(initial?.default_locale ?? 'en');
  const [defaultTimezone, setDefaultTimezone] = useState(initial?.default_timezone ?? 'UTC');
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugResult, setSlugResult] = useState<{ valid: boolean; available: boolean; suggestion: string } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const config: PlatformIdentityConfig = {
    name, slug, domain, description, data_residency: dataResidency,
    default_locale: defaultLocale, default_timezone: defaultTimezone,
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await store.SavePlatformIdentity({ identity: config });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const r = await store.ValidateStep('identity', { step_id: 'identity', identity: config });
      setErrors(r.errors ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  const handleCheckSlug = async () => {
    if (!slug.trim()) return;
    setIsCheckingSlug(true);
    try {
      const r = await ValidateSlug({ slug: slug.trim() });
      setSlugResult({ valid: r.valid, available: r.available, suggestion: r.suggestion });
    } finally {
      setIsCheckingSlug(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <Input label={t('bootstrap.step.identity.name')} value={name} onChange={(e) => setName(e.target.value)} required />
      <div className="flex items-end gap-2">
        <Input label={t('bootstrap.step.identity.slug')} value={slug} onChange={(e) => { setSlug(e.target.value); setSlugResult(null); }} required className="flex-1" />
        <Button type="button" variant="secondary" size="sm" onClick={handleCheckSlug} disabled={isCheckingSlug || !slug.trim()}>
          {isCheckingSlug ? t('common.loading') : t('bootstrap.step.identity.checkSlug')}
        </Button>
      </div>
      {slugResult && (
        <p className={`text-sm ${slugResult.valid && slugResult.available ? 'text-success' : 'text-destructive'}`}>
          {slugResult.valid && slugResult.available
            ? t('bootstrap.step.identity.slugAvailable')
            : t('bootstrap.step.identity.slugUnavailable', { suggestion: slugResult.suggestion || '' })}
        </p>
      )}
      <Input label={t('bootstrap.step.identity.domain')} value={domain} onChange={(e) => setDomain(e.target.value)} required />
      <Textarea label={t('bootstrap.step.identity.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      <Select
        label={t('bootstrap.step.identity.dataResidency')}
        value={dataResidency}
        onChange={(e) => setDataResidency(e.target.value as DataResidencyRegion)}
        options={DataResidencyRegionValues.filter((v) => v !== 'DATA_RESIDENCY_REGION_UNSPECIFIED').map((v) => ({
          value: v, label: t(`bootstrap.step.identity.residency.${enumSuffix(v, 'DATA_RESIDENCY_REGION_')}`),
        }))}
      />
      <div className="grid grid-cols-2 gap-3">
        <Input label={t('bootstrap.step.identity.defaultLocale')} value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)} />
        <Input label={t('bootstrap.step.identity.defaultTimezone')} value={defaultTimezone} onChange={(e) => setDefaultTimezone(e.target.value)} />
      </div>
      <ValidationErrors errors={errors} />
      <StepFooter isSaving={isSaving} isValidating={isValidating} onValidate={handleValidate} savedLabel={t('common.save')} />
    </form>
  );
}

// --- Step 3: Vault ---

export function VaultStepForm({ initial, onSaved }: { initial?: VaultInitConfig; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [shares, setShares] = useState(String(initial?.secret_shares || 5));
  const [threshold, setThreshold] = useState(String(initial?.secret_threshold || 3));
  const [provider, setProvider] = useState<AutoUnsealProvider>(initial?.auto_unseal?.provider ?? 'AUTO_UNSEAL_PROVIDER_UNSPECIFIED');
  const [keyId, setKeyId] = useState('');
  const [region, setRegion] = useState('');
  const [probe, setProbe] = useState<{ initialized: boolean; sealed: boolean } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isProbing, setIsProbing] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; error?: string; info?: string } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const buildAutoUnseal = (): AutoUnsealConfig => {
    const cfg: AutoUnsealConfig = {
      provider,
      aws_kms_key_id: '', aws_region: '', aws_access_key_id: '', aws_secret_access_key: '',
      gcp_kms_key_ring: '', gcp_kms_key: '', gcp_kms_project: '', gcp_kms_region: '', gcp_credentials_json: '',
      azure_vault_name: '', azure_key_name: '', azure_tenant_id: '', azure_client_id: '', azure_client_secret: '',
      yandex_kms_key_id: '', yandex_cloud_folder_id: '', yandex_service_account_key_json: '',
    };
    if (provider === 'AUTO_UNSEAL_PROVIDER_AWS_KMS') { cfg.aws_kms_key_id = keyId; cfg.aws_region = region; }
    if (provider === 'AUTO_UNSEAL_PROVIDER_GCP_KMS') { cfg.gcp_kms_key = keyId; cfg.gcp_kms_region = region; }
    if (provider === 'AUTO_UNSEAL_PROVIDER_AZURE_KEY_VAULT') { cfg.azure_key_name = keyId; cfg.azure_vault_name = region; }
    if (provider === 'AUTO_UNSEAL_PROVIDER_YANDEX_KMS') { cfg.yandex_kms_key_id = keyId; cfg.yandex_cloud_folder_id = region; }
    return cfg;
  };

  const config: VaultInitConfig = {
    secret_shares: parseInt(shares, 10) || 0,
    secret_threshold: parseInt(threshold, 10) || 0,
    auto_unseal: buildAutoUnseal(),
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await store.SaveVaultConfig({ vault: config });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const r = await store.ValidateStep('vault', { step_id: 'vault', vault: config });
      setErrors(r.errors ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  const handleProbe = async () => {
    setIsProbing(true);
    try {
      const r = await store.ProbeVaultStatus();
      setProbe({ initialized: r.vault_status?.initialized ?? false, sealed: r.vault_status?.sealed ?? true });
    } finally {
      setIsProbing(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const r = await store.TestAutoUnseal({ config: buildAutoUnseal() });
      setTestResult({ success: r.success, error: r.error, info: r.provider_info });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('bootstrap.step.vault.probeHint')}</p>
        <Button type="button" variant="ghost" size="sm" onClick={handleProbe} disabled={isProbing}>
          {isProbing ? t('common.loading') : t('bootstrap.step.vault.probe')}
        </Button>
      </div>
      {probe && (
        <p className="text-sm text-foreground">
          {t('bootstrap.step.vault.probeResult', {
            initialized: probe.initialized ? t('common.yes') : t('common.no'),
            sealed: probe.sealed ? t('common.yes') : t('common.no'),
          })}
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Input label={t('bootstrap.step.vault.shares')} type="number" min={1} max={10} value={shares} onChange={(e) => setShares(e.target.value)} />
        <Input label={t('bootstrap.step.vault.threshold')} type="number" min={1} max={10} value={threshold} onChange={(e) => setThreshold(e.target.value)} />
      </div>
      <Select
        label={t('bootstrap.step.vault.autoUnseal')}
        value={provider}
        onChange={(e) => setProvider(e.target.value as AutoUnsealProvider)}
        options={AutoUnsealProviderValues.map((v) => ({
          value: v, label: t(`bootstrap.step.vault.provider.${enumSuffix(v, 'AUTO_UNSEAL_PROVIDER_')}`),
        }))}
      />
      {provider !== 'AUTO_UNSEAL_PROVIDER_UNSPECIFIED' && provider !== 'AUTO_UNSEAL_PROVIDER_TRANSIT' && (
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('bootstrap.step.vault.keyId')} value={keyId} onChange={(e) => setKeyId(e.target.value)} />
          <Input label={t('bootstrap.step.vault.regionOrScope')} value={region} onChange={(e) => setRegion(e.target.value)} />
        </div>
      )}
      {provider !== 'AUTO_UNSEAL_PROVIDER_UNSPECIFIED' && (
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={handleTest} disabled={isTesting}>
            {isTesting ? t('common.loading') : t('bootstrap.step.vault.testConnection')}
          </Button>
          {testResult && (
            <span className={`text-sm ${testResult.success ? 'text-success' : 'text-destructive'}`}>
              {testResult.success ? (testResult.info || t('bootstrap.step.vault.testOk')) : (testResult.error || t('bootstrap.step.vault.testFailed'))}
            </span>
          )}
        </div>
      )}
      <ValidationErrors errors={errors} />
      <StepFooter isSaving={isSaving} isValidating={isValidating} onValidate={handleValidate} savedLabel={t('common.save')} />
    </form>
  );
}

// --- Step 4: Admin account ---

export function AdminStepForm({ initial, onSaved }: { initial?: AdminAccountConfig; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [email, setEmail] = useState(initial?.email ?? '');
  const [firstName, setFirstName] = useState(initial?.first_name ?? '');
  const [lastName, setLastName] = useState(initial?.last_name ?? '');
  const [password, setPassword] = useState('');
  const [sendConfirmation, setSendConfirmation] = useState(initial?.send_confirmation_email ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const config: AdminAccountConfig = { email, first_name: firstName, last_name: lastName, password, send_confirmation_email: sendConfirmation };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await store.SaveAdminAccount({ admin: config });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const r = await store.ValidateStep('admin', { step_id: 'admin', admin: config });
      setErrors(r.errors ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <Input label={t('bootstrap.step.admin.email')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <div className="grid grid-cols-2 gap-3">
        <Input label={t('bootstrap.step.admin.firstName')} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
        <Input label={t('bootstrap.step.admin.lastName')} value={lastName} onChange={(e) => setLastName(e.target.value)} />
      </div>
      <Input label={t('bootstrap.step.admin.password')} type="password" value={password} onChange={(e) => setPassword(e.target.value)} required helperText={t('bootstrap.step.admin.passwordHelp')} />
      <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
        <input type="checkbox" checked={sendConfirmation} onChange={(e) => setSendConfirmation(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
        {t('bootstrap.step.admin.sendConfirmation')}
      </label>
      <ValidationErrors errors={errors} />
      <StepFooter isSaving={isSaving} isValidating={isValidating} onValidate={handleValidate} savedLabel={t('common.save')} />
    </form>
  );
}

// --- Step 5: Products ---

export function ProductsStepForm({ initial, onSaved }: { initial?: ProductSelectionConfig; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [slugsText, setSlugsText] = useState((initial?.products ?? []).filter((p) => p.enabled).map((p) => p.slug).join(', '));
  const [resolved, setResolved] = useState(initial?.products ?? []);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const enabledSlugs = () => slugsText.split(',').map((s) => s.trim()).filter(Boolean);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const r = await store.SaveProductSelection({ enabled_slugs: enabledSlugs() });
      setResolved(r.resolved?.products ?? []);
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const r = await store.ValidateStep('products', {
        step_id: 'products',
        products: { products: enabledSlugs().map((slug) => ({ slug, name: slug, enabled: true, is_core: false, auto_enabled: false, auto_enabled_by: '' })) },
      });
      setErrors(r.errors ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-3">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      <Textarea
        label={t('bootstrap.step.products.field')}
        value={slugsText}
        onChange={(e) => setSlugsText(e.target.value)}
        rows={2}
        helperText={t('bootstrap.step.products.helper')}
      />
      {resolved.length > 0 && (
        <ul className="divide-y divide-border rounded-md border border-border">
          {resolved.map((p) => (
            <li key={p.slug} className="flex items-center justify-between px-3 py-1.5 text-sm">
              <span className="text-foreground">{p.name || p.slug}</span>
              <span className="text-xs text-muted-foreground">
                {p.is_core ? t('bootstrap.step.products.core') : p.auto_enabled ? t('bootstrap.step.products.autoEnabled', { by: p.auto_enabled_by }) : t('bootstrap.step.products.selected')}
              </span>
            </li>
          ))}
        </ul>
      )}
      <ValidationErrors errors={errors} />
      <StepFooter isSaving={isSaving} isValidating={isValidating} onValidate={handleValidate} savedLabel={t('common.save')} />
    </form>
  );
}

// --- Step 6: Security ---

function LdapFields({ ldap, setLdap }: { ldap: LdapConfig; setLdap: (v: LdapConfig) => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [isTesting, setIsTesting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; error?: string; users?: number; groups?: number } | null>(null);

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const r = await store.TestLdapConnection({ ldap });
      setResult({ success: r.success, error: r.error, users: r.users_found, groups: r.groups_found });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <Input label={t('bootstrap.step.security.ldap.url')} value={ldap.url} onChange={(e) => setLdap({ ...ldap, url: e.target.value })} />
      <Input label={t('bootstrap.step.security.ldap.bindDn')} value={ldap.bind_dn} onChange={(e) => setLdap({ ...ldap, bind_dn: e.target.value })} />
      <Input label={t('bootstrap.step.security.ldap.bindPassword')} type="password" value={ldap.bind_password} onChange={(e) => setLdap({ ...ldap, bind_password: e.target.value })} />
      <Input label={t('bootstrap.step.security.ldap.userSearchBase')} value={ldap.user_search_base} onChange={(e) => setLdap({ ...ldap, user_search_base: e.target.value })} />
      <div className="flex items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={handleTest} disabled={isTesting}>
          {isTesting ? t('common.loading') : t('bootstrap.step.security.testLdap')}
        </Button>
        {result && (
          <span className={`text-sm ${result.success ? 'text-success' : 'text-destructive'}`}>
            {result.success ? t('bootstrap.step.security.ldapOk', { users: result.users ?? 0, groups: result.groups ?? 0 }) : (result.error || t('bootstrap.step.security.testFailed'))}
          </span>
        )}
      </div>
    </div>
  );
}

function OidcFields({ oidc, setOidc }: { oidc: OidcConfig; setOidc: (v: OidcConfig) => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const [isTesting, setIsTesting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; error?: string; issuer?: string } | null>(null);

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const r = await store.TestOidcProvider({ oidc });
      setResult({ success: r.success, error: r.error, issuer: r.issuer });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="space-y-2 rounded-md border border-border p-3">
      <Input label={t('bootstrap.step.security.oidc.issuerUrl')} value={oidc.issuer_url} onChange={(e) => setOidc({ ...oidc, issuer_url: e.target.value })} />
      <Input label={t('bootstrap.step.security.oidc.clientId')} value={oidc.client_id} onChange={(e) => setOidc({ ...oidc, client_id: e.target.value })} />
      <Input label={t('bootstrap.step.security.oidc.clientSecret')} type="password" value={oidc.client_secret} onChange={(e) => setOidc({ ...oidc, client_secret: e.target.value })} />
      <div className="flex items-center gap-2">
        <Button type="button" variant="secondary" size="sm" onClick={handleTest} disabled={isTesting}>
          {isTesting ? t('common.loading') : t('bootstrap.step.security.testOidc')}
        </Button>
        {result && (
          <span className={`text-sm ${result.success ? 'text-success' : 'text-destructive'}`}>
            {result.success ? t('bootstrap.step.security.oidcOk', { issuer: result.issuer ?? '' }) : (result.error || t('bootstrap.step.security.testFailed'))}
          </span>
        )}
      </div>
    </div>
  );
}

export function SecurityStepForm({ initial, onSaved }: { initial?: SecurityConfig; onSaved: () => void }) {
  const { t } = useI18n();
  const store = useBootstrap();
  const enabledFromInitial = new Set((initial?.auth_methods ?? []).filter((m) => m.enabled).map((m) => m.type));
  const [enabledMethods, setEnabledMethods] = useState<Set<AuthMethodType>>(
    enabledFromInitial.size > 0 ? enabledFromInitial as Set<AuthMethodType> : new Set(['AUTH_METHOD_TYPE_LOCAL']),
  );
  const [ldap, setLdap] = useState<LdapConfig>(
    initial?.auth_methods?.find((m) => m.type === 'AUTH_METHOD_TYPE_LDAP')?.ldap
    ?? { url: '', bind_dn: '', bind_password: '', user_search_base: '', user_search_filter: '', group_search_base: '', group_search_filter: '', start_tls: false, skip_tls_verify: false, tls_ca_cert: '' },
  );
  const [oidc, setOidc] = useState<OidcConfig>(
    initial?.auth_methods?.find((m) => m.type === 'AUTH_METHOD_TYPE_OIDC')?.oidc
    ?? { issuer_url: '', client_id: '', client_secret: '', scopes: ['openid', 'profile', 'email'], redirect_uri: '', claim_mapping: {} },
  );
  const [requireEmailVerification, setRequireEmailVerification] = useState(initial?.require_email_verification ?? true);
  const [require2fa, setRequire2fa] = useState(initial?.require_2fa ?? false);
  const [enforcePasswordComplexity, setEnforcePasswordComplexity] = useState(initial?.enforce_password_complexity ?? true);
  const [sessionTimeout, setSessionTimeout] = useState(String(initial?.session_timeout_minutes || 30));
  const [maxFailedLogins, setMaxFailedLogins] = useState(String(initial?.max_failed_login_attempts || 5));
  const [tokenIssuer, setTokenIssuer] = useState(initial?.jwt?.token_issuer ?? '');
  const [accessTtl, setAccessTtl] = useState(String(initial?.jwt?.access_token_ttl_minutes || 15));
  const [refreshTtl, setRefreshTtl] = useState(String(initial?.jwt?.refresh_token_ttl_hours || 24));
  const [signingAlgorithm, setSigningAlgorithm] = useState<JwtSigningAlgorithm>(initial?.jwt?.signing_algorithm ?? 'JWT_SIGNING_ALGORITHM_EDDSA');
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const toggleMethod = (m: AuthMethodType) => {
    setEnabledMethods((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
  };

  const buildConfig = (): SecurityConfig => ({
    auth_methods: AuthMethodTypeValues.filter((v) => v !== 'AUTH_METHOD_TYPE_UNSPECIFIED').map((type) => ({
      type,
      enabled: enabledMethods.has(type),
      is_primary: type === 'AUTH_METHOD_TYPE_LOCAL',
      ldap: type === 'AUTH_METHOD_TYPE_LDAP' ? ldap : undefined,
      saml: undefined,
      oidc: type === 'AUTH_METHOD_TYPE_OIDC' ? oidc : undefined,
    })),
    require_email_verification: requireEmailVerification,
    require_2fa: require2fa,
    enforce_password_complexity: enforcePasswordComplexity,
    session_timeout_minutes: parseInt(sessionTimeout, 10) || 0,
    max_failed_login_attempts: parseInt(maxFailedLogins, 10) || 0,
    jwt: {
      token_issuer: tokenIssuer,
      access_token_ttl_minutes: parseInt(accessTtl, 10) || 0,
      refresh_token_ttl_hours: parseInt(refreshTtl, 10) || 0,
      signing_algorithm: signingAlgorithm,
    },
  });

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await store.SaveSecurityConfig({ security: buildConfig() });
      onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidate = async () => {
    setIsValidating(true);
    try {
      const r = await store.ValidateStep('security', { step_id: 'security', security: buildConfig() });
      setErrors(r.errors ?? []);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="space-y-4">
      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-foreground">{t('bootstrap.step.security.authMethods')}</p>
        {AuthMethodTypeValues.filter((v) => v !== 'AUTH_METHOD_TYPE_UNSPECIFIED').map((m) => (
          <label key={m} className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
            <input
              type="checkbox"
              checked={enabledMethods.has(m)}
              disabled={m === 'AUTH_METHOD_TYPE_LOCAL'}
              onChange={() => toggleMethod(m)}
              className="h-4 w-4 rounded border-input text-primary focus:ring-ring"
            />
            {t(`bootstrap.step.security.method.${enumSuffix(m, 'AUTH_METHOD_TYPE_')}`)}
          </label>
        ))}
      </div>

      {enabledMethods.has('AUTH_METHOD_TYPE_LDAP') && <LdapFields ldap={ldap} setLdap={setLdap} />}
      {enabledMethods.has('AUTH_METHOD_TYPE_OIDC') && <OidcFields oidc={oidc} setOidc={setOidc} />}

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-foreground">{t('bootstrap.step.security.policies')}</p>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
          <input type="checkbox" checked={requireEmailVerification} onChange={(e) => setRequireEmailVerification(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
          {t('bootstrap.step.security.requireEmailVerification')}
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
          <input type="checkbox" checked={require2fa} onChange={(e) => setRequire2fa(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
          {t('bootstrap.step.security.require2fa')}
        </label>
        <label className="flex items-center gap-2 cursor-pointer text-sm text-foreground">
          <input type="checkbox" checked={enforcePasswordComplexity} onChange={(e) => setEnforcePasswordComplexity(e.target.checked)} className="h-4 w-4 rounded border-input text-primary focus:ring-ring" />
          {t('bootstrap.step.security.enforcePasswordComplexity')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('bootstrap.step.security.sessionTimeout')} type="number" min={0} value={sessionTimeout} onChange={(e) => setSessionTimeout(e.target.value)} />
          <Input label={t('bootstrap.step.security.maxFailedLogins')} type="number" min={0} value={maxFailedLogins} onChange={(e) => setMaxFailedLogins(e.target.value)} />
        </div>
      </div>

      <div className="space-y-1.5">
        <p className="text-sm font-medium text-foreground">{t('bootstrap.step.security.jwt')}</p>
        <Input label={t('bootstrap.step.security.tokenIssuer')} value={tokenIssuer} onChange={(e) => setTokenIssuer(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('bootstrap.step.security.accessTtl')} type="number" min={1} value={accessTtl} onChange={(e) => setAccessTtl(e.target.value)} />
          <Input label={t('bootstrap.step.security.refreshTtl')} type="number" min={1} value={refreshTtl} onChange={(e) => setRefreshTtl(e.target.value)} />
        </div>
        <Select
          label={t('bootstrap.step.security.signingAlgorithm')}
          value={signingAlgorithm}
          onChange={(e) => setSigningAlgorithm(e.target.value as JwtSigningAlgorithm)}
          options={JwtSigningAlgorithmValues.filter((v) => v !== 'JWT_SIGNING_ALGORITHM_UNSPECIFIED').map((v) => ({ value: v, label: v.replace('JWT_SIGNING_ALGORITHM_', '') }))}
        />
      </div>

      <ValidationErrors errors={errors} />
      <StepFooter isSaving={isSaving} isValidating={isValidating} onValidate={handleValidate} savedLabel={t('common.save')} />
    </form>
  );
}
